import { useIntl } from 'react-intl';
import { Modal } from 'react-bootstrap';
import { useState, useEffect } from 'react';
import { AmountCreditNoteRequestModel } from '../models/AmountCreditNoteRequestModel';
import { useQuery } from '@tanstack/react-query';
import { listDistributors } from '../../distributors/services/DistributorCRUD';
import { listTaxes, listDistributorShippingAddresses } from '../../distributors';
import { createAmountCreditNote } from '../services/CreditNoteCRUD';
import { createAmountInvoice } from '../services/InvoiceCRUD';
import { toast } from 'react-toastify';

export enum AmountInvoiceKind {
  Invoice = 'invoice',
  CreditNote = 'credit-note',
}

interface PropTypes {
  visible?: boolean;
  kind: AmountInvoiceKind;
  onClose?: () => void;
  onCreated?: (invoice: any) => void;
}

export default function AmountInvoiceModal({
  visible = false,
  kind,
  onClose = () => {},
  onCreated = () => {},
}: PropTypes) {
  const intl = useIntl();
  const [loading, setLoading] = useState(false);
  const [concept, setConcept] = useState<string>('');
  const [amount, setAmount] = useState<number | null>(null);
  const [fiscalPositions, setFiscalPositions] = useState<number[]>([]);
  const { data: distributors, isLoading: isLoadingDistributors } = useQuery({
    queryKey: ['distributors'],
    queryFn: () => listDistributors('active'),
    select: ({ data }) =>
      data.map((item) => ({
        label: item.name,
        value: item.id,
      })),
  });
  const { data: taxes, isLoading: isLoadingTaxes } = useQuery({
    queryKey: ['taxes'],
    queryFn: listTaxes,
    select: ({ data }) =>
      data.map((item) => ({
        label: `${item.name} (${Math.round(item.vat * 10000) / 100}%)`,
        value: item.id,
      })),
  });

  const [request, setRequest] = useState<AmountCreditNoteRequestModel>({
    distributor: null,
    shipping_address: null,
    concepts: [],
  });

  const { data: shippingAddresses, isLoading: isLoadingShippingAddresses } =
    useQuery({
      queryKey: ['distributor-shipping-addresses', request.distributor],
      queryFn: () =>
        listDistributorShippingAddresses(request.distributor as number),
      enabled: !!request.distributor,
      select: ({ data }) => data,
    });

  const isCreditNote = kind === AmountInvoiceKind.CreditNote;

  useEffect(() => {
    if (visible) {
      setLoading(false);
      setConcept('');
      setAmount(null);
      setFiscalPositions([]);
      setRequest({
        distributor: null,
        shipping_address: null,
        concepts: [],
      });
    }
  }, [visible]);

  useEffect(() => {
    if (shippingAddresses?.length && !request.shipping_address) {
      setRequest((prev) => ({
        ...prev,
        shipping_address: shippingAddresses[0].id,
      }));
    }
  }, [shippingAddresses]);

  function isInvalid() {
    return (
      !request.distributor ||
      !request.shipping_address ||
      request.concepts.length === 0
    );
  }

  async function onSubmit() {
    setLoading(true);
    try {
      const createFn = isCreditNote
        ? createAmountCreditNote
        : createAmountInvoice;
      const newlyCreated = await createFn(request);
      toast.success(
        intl.formatMessage({
          id: isCreditNote
            ? 'amountInvoice.creditNoteCreatedSuccessfully'
            : 'amountInvoice.invoiceCreatedSuccessfully',
        })
      );
      onCreated(newlyCreated);
    } catch (error) {
      toast.error(
        intl.formatMessage({
          id: isCreditNote
            ? 'amountInvoice.creditNoteCreationFailed'
            : 'amountInvoice.invoiceCreationFailed',
        })
      );
    } finally {
      setLoading(false);
    }
  }

  async function addConcept() {
    if (concept.length > 0 && amount > 0) {
      setRequest((prev) => ({
        ...prev,
        concepts: [
          ...prev.concepts,
          {
            fiscal_positions: fiscalPositions,
            amount,
            description: concept,
          },
        ],
      }));
      setConcept('');
      setAmount(0);
      setFiscalPositions([]);
    }
  }

  function removeConcept(index: number) {
    setRequest((prev) => ({
      ...prev,
      concepts: prev.concepts.filter((_, i) => i !== index),
    }));
  }

  return (
    <Modal show={visible} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>
          {intl.formatMessage({
            id: isCreditNote
              ? 'amountInvoice.creditNoteTitle'
              : 'amountInvoice.invoiceTitle',
          })}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="form-group">
          <label className="form-label">
            {intl.formatMessage({ id: 'amountInvoice.selectDistributor' })}
          </label>
          <select
            value={request.distributor || ''}
            className="form-select"
            required
            disabled={isLoadingDistributors}
            onChange={(e) =>
              setRequest({
                ...request,
                distributor: parseInt(e.target.value, 10),
                shipping_address: null,
              })
            }
          >
            <option value="" disabled>
              {intl.formatMessage({
                id: 'amountInvoice.distributorPlaceholder',
              })}
            </option>
            {distributors?.map((distributor) => (
              <option key={distributor.value} value={distributor.value}>
                {distributor.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group mt-4">
          <label className="form-label">
            {intl.formatMessage({ id: 'amountInvoice.selectShippingAddress' })}
          </label>
          <select
            value={request.shipping_address || ''}
            className="form-select"
            required
            disabled={!request.distributor || isLoadingShippingAddresses}
            onChange={(e) =>
              setRequest({
                ...request,
                shipping_address: parseInt(e.target.value, 10),
              })
            }
          >
            <option value="" disabled>
              {intl.formatMessage({
                id: 'amountInvoice.shippingAddressPlaceholder',
              })}
            </option>
            {shippingAddresses?.map((shippingAddress) => (
              <option key={shippingAddress.id} value={shippingAddress.id}>
                {shippingAddress.name}, {shippingAddress.street},{' '}
                {shippingAddress.postcode} {shippingAddress.city}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group mt-8">
          <h5 className="mb-5">
            {intl.formatMessage({ id: 'amountInvoice.concepts' })}
          </h5>
          <div className="form-group w-100">
            <label className="form-label">
              {intl.formatMessage({ id: 'amountInvoice.concept' })}
            </label>
            <input
              maxLength={140}
              required
              id="concept"
              type="text"
              className="form-control"
              placeholder={intl.formatMessage({
                id: 'amountInvoice.conceptPlaceholder',
              })}
              value={concept}
              onChange={(e) => setConcept(e.target.value)}
            />
          </div>
          <div className="d-flex gap-4 mb-5 space-between mt-5">
            <div className="form-group">
              <label className="form-label">
                {intl.formatMessage({ id: 'amountInvoice.amount' })}
              </label>
              <div className="input-group">
                <input
                  id="amount"
                  required
                  className="form-control"
                  type="number"
                  min={0}
                  value={amount}
                  onChange={(e) => setAmount(parseFloat(e.target.value))}
                />
                <span className="input-group-text">€</span>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                {intl.formatMessage({ id: 'amountInvoice.fiscalPosition' })}
              </label>
              <div
                className="border rounded p-4 bg-light"
                style={{ maxHeight: '200px', overflowY: 'auto' }}
              >
                {isLoadingTaxes ? (
                  <p className="text-muted mb-0">
                    {intl.formatMessage({ id: 'common.loading' })}
                  </p>
                ) : taxes?.length === 0 ? (
                  <p className="text-muted mb-0">
                    {intl.formatMessage({
                      id: 'amountInvoice.noTaxesAvailable',
                    })}
                  </p>
                ) : (
                  <>
                    <div className="form-check mb-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="no-tax"
                        checked={fiscalPositions.length === 0}
                        onChange={() => setFiscalPositions([])}
                      />
                      <label className="form-check-label" htmlFor="no-tax">
                        {intl.formatMessage({ id: 'amountInvoice.noTax' })}
                      </label>
                    </div>
                    {taxes?.map((tax) => (
                      <div key={tax.value} className="form-check mb-2">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id={`tax-${tax.value}`}
                          checked={fiscalPositions.includes(tax.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFiscalPositions([
                                ...fiscalPositions,
                                tax.value,
                              ]);
                            } else {
                              setFiscalPositions(
                                fiscalPositions.filter((id) => id !== tax.value)
                              );
                            }
                          }}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`tax-${tax.value}`}
                        >
                          {tax.label}
                        </label>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>

          <button className="btn btn-light-primary mb-5" onClick={addConcept}>
            <span className="fa-icon fas fa-plus me-3" />
            {intl.formatMessage({ id: 'amountInvoice.addConcept' })}
          </button>
          {!request.concepts.length && (
            <p className="text-muted">
              {intl.formatMessage({ id: 'amountInvoice.noConceptsAdded' })}
            </p>
          )}
          {request.concepts.length > 0 && (
            <div className="table-responsive bg-light-primary px-8 py-4 rounded">
              <table className="table">
                <thead className="fw-bolder">
                  <tr>
                    <th>
                      {intl.formatMessage({ id: 'amountInvoice.concept' })}
                    </th>
                    <th>
                      {intl.formatMessage({ id: 'amountInvoice.amount' })}
                    </th>
                    <th>
                      {intl.formatMessage({
                        id: 'amountInvoice.fiscalPosition',
                      })}
                    </th>
                    <th>
                      {intl.formatMessage({ id: 'amountInvoice.remove' })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {request.concepts.map((concept, index) => (
                    <tr key={index}>
                      <td>{concept.description}</td>
                      <td>{concept.amount}€</td>
                      <td>
                        {concept.fiscal_positions.length === 0 ? (
                          <span className="badge badge-secondary">
                            {intl.formatMessage({ id: 'amountInvoice.noTax' })}
                          </span>
                        ) : (
                          <div className="d-flex flex-wrap gap-2">
                            {concept.fiscal_positions.map((taxId) => (
                              <span key={taxId} className="badge badge-primary">
                                {
                                  taxes?.find((tax) => tax.value === taxId)
                                    ?.label
                                }
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="text-center">
                        <a
                          className="text-primary cursor-pointer"
                          onClick={() => removeConcept(index)}
                        >
                          <span className="fa-icon fas fa-times me-2" />
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-tertiary" onClick={onClose}>
          {intl.formatMessage({ id: 'amountInvoice.cancel' })}
        </button>
        <button
          disabled={isInvalid() || loading}
          className="btn btn-primary"
          onClick={onSubmit}
        >
          {intl.formatMessage({
            id: isCreditNote
              ? 'amountInvoice.generateCreditNote'
              : 'amountInvoice.generateInvoice',
          })}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
